import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  TextField,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import HistoryEduOutlinedIcon from "@mui/icons-material/HistoryEduOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import DashboardLayout from "../layouts/DashboardLayout";
import { useThemeColors } from "../context/ThemeContext";
import GlassCard from "../components/GlassCard";
import TableSkeleton from "../components/TableSkeleton";
import { glassRowSx } from "../components/glassRow";
import api from "../api";
import { useActivityLog } from "../hooks/queries";
import { HEADING_FONT } from "../theme/fonts";

// Brand gradient for activity entry accents + icons.
const ACTIVITY_GRADIENT = "linear-gradient(135deg, #818cf8 0%, #6366f1 100%)";

function formatRelative(dateStr) {
  if (!dateStr) return "—";
  const then = new Date(dateStr.replace(" ", "T"));
  if (isNaN(then.getTime())) return dateStr;
  const diffMs = Date.now() - then.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD}d ago`;
  const diffMo = Math.floor(diffD / 30);
  return `${diffMo}mo ago`;
}

function formatAbsolute(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr.replace(" ", "T"));
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatClock(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr.replace(" ", "T"));
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function groupByDay(entries) {
  const groups = new Map();
  for (const e of entries) {
    if (!e.createdAt) continue;
    const dateKey = e.createdAt.slice(0, 10); // YYYY-MM-DD
    if (!groups.has(dateKey)) groups.set(dateKey, []);
    groups.get(dateKey).push(e);
  }
  return [...groups.entries()].map(([date, items]) => ({ date, items }));
}

// Turn the /activity-log response into { entries, total } for the current page.
// The backend normally returns { rows, total }; if an older build returns a
// bare array, we filter (by domain name + activity text) + paginate on the
// client so the page still works during a partial deploy.
function normalizeActivity(data, page, pageSize, debouncedSearch, domainLookup) {
  if (!data) return { entries: [], total: 0 };
  if (Array.isArray(data)) {
    const q = debouncedSearch.trim().toLowerCase();
    const matched = q
      ? data.filter((e) => {
          const domainName = (domainLookup.get(e.domainId) || "").toLowerCase();
          return (
            domainName.includes(q) ||
            (e.activityText || "").toLowerCase().includes(q)
          );
        })
      : data;
    const start = (page - 1) * pageSize;
    return {
      entries: matched.slice(start, start + pageSize),
      total: matched.length,
    };
  }
  return { entries: data.rows || [], total: data.total || 0 };
}

function ActivityLog() {
  const colors = useThemeColors();
  // Server-side pagination: `entries` is just the current page, `total` is the
  // count matching the active search — both returned by the backend.
  const [domainLookup, setDomainLookup] = useState(new Map());
  const [search, setSearch] = useState("");
  // Debounced copy of `search` so we hit the API after the user stops typing,
  // not on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Build the query params from the active page + search. React Query
  // re-fetches automatically whenever this object changes — and serves the
  // cache when we revisit the same combination.
  const params = useMemo(() => {
    const p = { limit: pageSize, offset: (page - 1) * pageSize };
    if (debouncedSearch.trim()) p.search = debouncedSearch.trim();
    return p;
  }, [page, pageSize, debouncedSearch]);

  // placeholderData keeps the previous page on screen while the next one loads,
  // so paging/searching never flashes an empty list.
  const activityQuery = useActivityLog(params, {
    placeholderData: (prev) => prev,
  });
  const { entries, total } = useMemo(
    () =>
      normalizeActivity(
        activityQuery.data,
        page,
        pageSize,
        debouncedSearch,
        domainLookup
      ),
    [activityQuery.data, page, pageSize, debouncedSearch, domainLookup]
  );
  const loading = activityQuery.isFetching;

  // Load the domains lookup once (full list, independent of the current page)
  // so we can resolve domain names for display.
  useEffect(() => {
    api
      .get("/domains")
      .then((domains) =>
        setDomainLookup(
          new Map(domains.map((d) => [d.id, d.name || "(unknown)"]))
        )
      )
      .catch(() => {
        // Non-fatal — entries fall back to "Domain #<id>".
      });
  }, []);

  // Debounce the search box → debouncedSearch.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 when the (debounced) search or page size changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);

  // If the current page falls past the end, step back so we never show an
  // empty page.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const startIndex = (safePage - 1) * pageSize;
  const rangeStart = total === 0 ? 0 : startIndex + 1;
  const rangeEnd = Math.min(startIndex + pageSize, total);
  const groupedDays = groupByDay(entries);

  function buildPageList(current, total) {
    const pages = new Set([1, total, current, current - 1, current + 1]);
    const sorted = [...pages]
      .filter((n) => n >= 1 && n <= total)
      .sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    for (const n of sorted) {
      if (n - prev > 1) out.push("...");
      out.push(n);
      prev = n;
    }
    return out;
  }
  const pageList = buildPageList(safePage, totalPages);

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: colors.cardBg,
      color: colors.text,
      borderRadius: 2,
      "& fieldset": { borderColor: colors.cardBorder },
      "&:hover fieldset": { borderColor: colors.cardBorderHover },
      "&.Mui-focused fieldset": { borderColor: "#818cf8" },
    },
    "& input": { color: colors.text },
  };

  return (
    <DashboardLayout>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 4, justifyContent: "space-between" }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: HEADING_FONT,
              fontWeight: 700,
              color: colors.text,
              letterSpacing: "-0.5px",
            }}
          >
            Activity Log
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.textMuted, mt: 0.5 }}
          >
            {total} note
            {total === 1 ? "" : "s"} · renewal reminders, client
            follow-ups, and other domain activity
          </Typography>
        </Box>
      </Stack>

      {/* Search bar */}
      <GlassCard sx={{ p: 2, mb: 3 }}>
        <TextField
          placeholder="Search by domain name or activity text..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          sx={fieldSx}
          InputProps={{
            startAdornment: (
              <SearchIcon
                sx={{ color: colors.textFaint, mr: 1, fontSize: 20 }}
              />
            ),
          }}
        />
      </GlassCard>

      {/* Empty state */}
      {entries.length === 0 && loading ? (
        <GlassCard sx={{ overflow: "hidden" }}>
          <TableSkeleton rows={pageSize || 8} columns={3} />
        </GlassCard>
      ) : entries.length === 0 ? (
        <GlassCard sx={{ py: 8, textAlign: "center" }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              mx: "auto",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#818cf8",
              background: "rgba(129, 140, 248, 0.12)",
              border: "1px solid rgba(129, 140, 248, 0.25)",
            }}
          >
            <HistoryEduOutlinedIcon sx={{ fontSize: 36 }} />
          </Box>
          <Typography
            sx={{
              fontFamily: HEADING_FONT,
              fontSize: 16,
              fontWeight: 700,
              color: colors.text,
              mb: 0.5,
            }}
          >
            {debouncedSearch.trim() ? "No matches found" : "No activity yet"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
            {debouncedSearch.trim()
              ? "Try a different search term"
              : "Activity notes will appear here as they are logged"}
          </Typography>
        </GlassCard>
      ) : (
        <GlassCard sx={{ overflow: "hidden" }}>
          {/* Timeline grouped by day */}
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {groupedDays.map((group) => (
              <Box key={group.date} sx={{ mb: 4, "&:last-child": { mb: 0 } }}>
                {/* Day header */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: colors.textMuted,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {new Date(group.date).toLocaleDateString("en-IN", {
                      weekday: "short",
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      height: "1px",
                      background: colors.divider,
                    }}
                  />
                  <Chip
                    size="small"
                    label={`${group.items.length} entr${
                      group.items.length === 1 ? "y" : "ies"
                    }`}
                    sx={{
                      fontWeight: 700,
                      fontSize: 11,
                      height: 22,
                      background: colors.hover,
                      color: colors.textMuted,
                    }}
                  />
                </Stack>

                {/* Day entries — vertical timeline. A continuous line runs down
                    the left edge; each entry hangs off a gradient node. */}
                <Box
                  sx={{
                    position: "relative",
                    pl: { xs: 4, md: 5 },
                    // The timeline spine.
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 18,
                      bottom: 18,
                      left: { xs: 17, md: 21 },
                      width: "2px",
                      background: colors.divider,
                    },
                  }}
                >
                  <Stack spacing={1.5}>
                    {group.items.map((entry, idx) => {
                      const domainName =
                        domainLookup.get(entry.domainId) ||
                        `Domain #${entry.domainId}`;

                      return (
                        <Box
                          key={entry.id}
                          sx={{ position: "relative" }}
                        >
                          {/* Timeline node — sits on the spine */}
                          <Box
                            sx={{
                              position: "absolute",
                              left: { xs: -31, md: -39 },
                              top: 14,
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              background: ACTIVITY_GRADIENT,
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              border: `3px solid ${colors.cardBg}`,
                              boxShadow:
                                "0 4px 12px rgba(99, 102, 241, 0.35)",
                              zIndex: 1,
                            }}
                          >
                            <HistoryEduOutlinedIcon sx={{ fontSize: 18 }} />
                          </Box>

                          {/* Entry card */}
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: `1px solid ${colors.divider}`,
                              ...glassRowSx({
                                idx,
                                accent: ACTIVITY_GRADIENT,
                                hoverBg: colors.hover,
                              }),
                            }}
                          >
                            {/* Top row: domain link + actor + timestamp */}
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              spacing={1}
                              sx={{ mb: 0.75 }}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ flexWrap: "wrap", minWidth: 0 }}
                                useFlexGap
                              >
                                <Box
                                  component="a"
                                  href={`/domains/${entry.domainId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    textDecoration: "none",
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: colors.text,
                                    "&:hover": {
                                      color: "#818cf8",
                                      "& .ext-icon": { opacity: 1 },
                                    },
                                  }}
                                >
                                  <LanguageIcon
                                    sx={{ fontSize: 14, color: "#818cf8" }}
                                  />
                                  {domainName}
                                  <OpenInNewIcon
                                    className="ext-icon"
                                    sx={{
                                      fontSize: 11,
                                      opacity: 0,
                                      transition: "opacity 0.15s ease",
                                    }}
                                  />
                                </Box>

                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                  sx={{ color: colors.textFaint }}
                                >
                                  <PersonOutlineOutlinedIcon
                                    sx={{ fontSize: 12 }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: 11,
                                      color: colors.textMuted,
                                    }}
                                  >
                                    Admin
                                  </Typography>
                                </Stack>
                              </Stack>

                              {/* Timestamp — clock time, tooltip = full date */}
                              <Tooltip title={formatAbsolute(entry.createdAt)}>
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    color: colors.textMuted,
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                  }}
                                >
                                  {formatClock(entry.createdAt)} ·{" "}
                                  {formatRelative(entry.createdAt)}
                                </Typography>
                              </Tooltip>
                            </Stack>

                            {/* Activity text */}
                            <Typography
                              sx={{
                                fontSize: 14,
                                color: colors.text,
                                lineHeight: 1.45,
                                wordBreak: "break-word",
                              }}
                            >
                              {entry.activityText}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Pagination footer */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "center" }}
            spacing={2}
            sx={{
              width: "100%",
              justifyContent: "space-between",
              px: 3,
              py: 2,
              borderTop: `1px solid ${colors.cardBorder}`,
              background: colors.hover,
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                Showing{" "}
                <Box component="span" sx={{ fontWeight: 700, color: colors.text }}>
                  {rangeStart}–{rangeEnd}
                </Box>{" "}
                of{" "}
                <Box component="span" sx={{ fontWeight: 700, color: colors.text }}>
                  {total}
                </Box>
              </Typography>
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center">
              <Select
                size="small"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                MenuProps={{
                  anchorOrigin: { vertical: "top", horizontal: "left" },
                  transformOrigin: { vertical: "bottom", horizontal: "left" },
                  PaperProps: {
                    sx: {
                      mb: 0.5,
                      minWidth: 72,
                      borderRadius: 2,
                      background: colors.menuBg,
                      border: `1px solid ${colors.cardBorder}`,
                      boxShadow: colors.shadowLg,
                      "& .MuiList-root": { py: 0.5 },
                      "& .MuiMenuItem-root": {
                        fontSize: 13,
                        color: colors.text,
                        mx: 0.5,
                        my: 0.25,
                        minHeight: 32,
                        borderRadius: 1,
                        justifyContent: "center",
                        "&:hover": { background: colors.hover },
                        "&.Mui-selected": {
                          background: "rgba(129, 140, 248, 0.14)",
                          color: "#818cf8",
                          fontWeight: 700,
                          "&:hover": { background: "rgba(129, 140, 248, 0.2)" },
                        },
                      },
                    },
                  },
                }}
                sx={{
                  mr: 0.5,
                  height: 32,
                  minWidth: 62,
                  fontSize: 13,
                  fontWeight: 700,
                  color: colors.text,
                  background: colors.cardBg,
                  borderRadius: 2,
                  transition: "border-color 0.15s ease, background 0.15s ease",
                  "& .MuiSelect-select": {
                    py: 0,
                    pl: 1.5,
                    pr: "28px !important",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.cardBorder,
                  },
                  "&:hover": { background: colors.hover },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.cardBorderHover,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#818cf8",
                    borderWidth: "1px",
                  },
                  "& .MuiSvgIcon-root": { color: colors.textMuted, right: 4 },
                }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <MenuItem key={n} value={n} sx={{ fontSize: 13 }}>
                    {n}
                  </MenuItem>
                ))}
              </Select>

              <Tooltip title="First page">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === 1}
                    onClick={() => setPage(1)}
                    sx={{
                      color: colors.textMuted,
                      "&:hover": { background: colors.cardBg, color: "#818cf8" },
                    }}
                  >
                    <FirstPageIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Previous">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    sx={{
                      color: colors.textMuted,
                      "&:hover": { background: colors.cardBg, color: "#818cf8" },
                    }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>

              {pageList.map((p, i) =>
                p === "..." ? (
                  <Box
                    key={`ellipsis-${i}`}
                    sx={{ px: 1, fontSize: 13, color: colors.textFaint }}
                  >
                    …
                  </Box>
                ) : (
                  <Box
                    key={p}
                    component="button"
                    onClick={() => setPage(p)}
                    sx={{
                      minWidth: 32,
                      height: 32,
                      px: 1,
                      borderRadius: 1.5,
                      border: "1px solid",
                      borderColor:
                        p === safePage ? "#818cf8" : "transparent",
                      background:
                        p === safePage
                          ? "rgba(79, 70, 229, 0.12)"
                          : "transparent",
                      color: p === safePage ? "#818cf8" : colors.text,
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        background:
                          p === safePage
                            ? "rgba(79, 70, 229, 0.18)"
                            : colors.cardBg,
                      },
                    }}
                  >
                    {p}
                  </Box>
                )
              )}

              <Tooltip title="Next">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === totalPages}
                    onClick={() =>
                      setPage((p) => Math.min(totalPages, p + 1))
                    }
                    sx={{
                      color: colors.textMuted,
                      "&:hover": { background: colors.cardBg, color: "#818cf8" },
                    }}
                  >
                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Last page">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === totalPages}
                    onClick={() => setPage(totalPages)}
                    sx={{
                      color: colors.textMuted,
                      "&:hover": { background: colors.cardBg, color: "#818cf8" },
                    }}
                  >
                    <LastPageIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </GlassCard>
      )}
    </DashboardLayout>
  );
}

export default ActivityLog;
